import { Info } from "lucide-react";
import { useMemo } from "react";
import clsx from "clsx";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import RiskLevelInfo from "./RiskLevelInfo";
import { riskLevelLabel } from "./severity";
import { WebTrackerRiskLevel } from "./types";

function RiskIcon() {
  return <Info className="shrink-0 opacity-70" size={14} aria-hidden />;
}

type RiskInfoMode = "description" | "criteria" | "none";

type RiskProps = {
  description: string | JSX.Element;
  riskDisplayNameOverride?: string | null;
  riskLevel: WebTrackerRiskLevel;
  infoMode?: RiskInfoMode;
  className?: string;
};

function Risk({
  description,
  riskLevel,
  riskDisplayNameOverride = null,
  infoMode = "description",
  className,
}: RiskProps) {
  const riskLevelClass = useMemo(() => {
    switch (riskLevel) {
      case "highest":
      case "high":
        return "bg-status-error-tint text-status-error";
      case "medium":
        return "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300";
      case "low":
        return "bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-300";
      case "removed":
        return "border-borderSubtle bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border";
      case "unknown":
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
    }
  }, [riskLevel]);

  const tooltipTitle =
    typeof description === "string" ? (
      <span className="text-sm">{description}</span>
    ) : (
      description
    );

  const tag = (
    <span
      className={clsx(
        "text-label-xs inline-flex items-center gap-1 rounded-full px-2 py-1",
        infoMode === "criteria" && "cursor-pointer",
        riskLevelClass,
        className,
      )}
    >
      {riskDisplayNameOverride !== null ? (
        <span>{riskDisplayNameOverride}</span>
      ) : (
        <span>{riskLevelLabel[riskLevel]}</span>
      )}
      <RiskIcon />
    </span>
  );

  if (infoMode === "criteria") {
    return <RiskLevelInfo currentLevel={riskLevel} trigger={tag} />;
  }

  if (infoMode === "none") {
    return tag;
  }

  return <Tooltip content={tooltipTitle}>{tag}</Tooltip>;
}

export default Risk;
